import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { GET_ORGANIZATION_VOTING_AREAS } from 'src/graphql/campaign_voting_areas/queries';

// Geography filter key (as used by the voter list) -> the voting_areas column
// whose presence marks the org as scoped on that geography type. There is no
// explicit "type" column on voting_areas (see VotingAreaFields), so a type is
// "in scope" iff at least one of the org's rows has that column populated.
const SCOPE_TYPE_COLUMNS = {
  counties: 'county_id',
  cities: 'city_id',
  cityCouncils: 'city_council_id',
  congressionalDistricts: 'congressional_district_id',
  lowerDistricts: 'state_leg_lower_district_id',
  lowerSubDistricts: 'state_leg_lower_sub_district_id',
  upperDistricts: 'state_leg_upper_district_id',
  precincts: 'precinct_id',
  precinctCodes: 'precinctcode',
  jurisnames: 'jurisname',
  wards: 'ward',
  // Types backed by the ri_customdistrict_new slots rather than a thin-table column (see
  // CUSTOM_DISTRICT_MAPPINGS). An org scopes on them through these voting_areas columns just
  // like any other geography.
  schoolDistricts: 'school_district',
  schoolBoards: 'school_board',
  countyCouncils: 'county_council',
  countyCommissioners: 'county_commissioner',
  advisoryNeighborhoodDistricts: 'advisory_neighborhood_district',
  // Not `justice_of_the_peace` as votingAreaFields claims — that column is not on the
  // VotingAreaFields fragment, so reading it would silently never mark the type in scope.
  justicesOfThePeace: 'justice_of_the_peace_district',
};

// How many distinct areas of each type the org covers. Deduplicated like the backend's own
// `uniq` in getGeoScopesForOrganization, so "3 counties" here is the same 3 the geo-scope
// restricts to.
const deriveScopeCounts = (votingAreas) =>
  Object.fromEntries(
    Object.entries(SCOPE_TYPE_COLUMNS).map(([key, column]) => [
      key,
      new Set(
        votingAreas
          .map((va) => va?.[column])
          // Treat empty strings as absent so bad-data rows on the string columns
          // (precinctcode/jurisname/ward) don't count as in-scope - matches the
          // backend's truthy check in appendVotingAreaToGeoScopes. `!= null` (not
          // truthy) is kept so a legitimate `0` id column would still count.
          .filter((value) => value != null && value !== '')
      ).size,
    ])
  );

/**
 * An org's scoped geography level, inferred from which voting-area columns
 * are populated across its `organization_voting_areas` rows (there's no
 * explicit "level" enum in the schema - see VotingAreaFields). Works
 * identically for managers and superadmin since it's queried by
 * `organizationId` directly, not derived from a JWT (unlike
 * `OrganizationContext.geoScopes`, which is always empty for superadmin).
 *
 * Per product direction: state/county/congressional-district-scoped orgs
 * default the Precinct detail table to a county-level rollup (a
 * congressional district or county can span many precincts); house-
 * district-scoped, senate-district-scoped, and narrower (precinct-scoped)
 * orgs default straight to the precinct-level table.
 */
const scopeLevelFromVotingAreas = (votingAreas) => {
  const isPrecinctScoped = votingAreas.some(
    (va) =>
      va?.precinct_id != null ||
      va?.state_leg_lower_district_id != null ||
      va?.state_leg_upper_district_id != null
  );
  return isPrecinctScoped ? 'precinct' : 'county';
};

export const useOrganizationVotingAreas = (organizationId) => {
  const { data: organizationVotingAreas, loading } = useQuery(GET_ORGANIZATION_VOTING_AREAS, {
    variables: {
      organizationId: organizationId,
    },
    skip: !organizationId,
  });
  const votingAreas =
    organizationVotingAreas?.rows?.map((r) => r.organization_voting_areas_voting_area) ?? [];
  const state = votingAreas[0]?.voting_areas_state?.name;
  const scopeLevel = scopeLevelFromVotingAreas(votingAreas);
  const scopeCounts = useMemo(() => deriveScopeCounts(votingAreas), [organizationVotingAreas]); // eslint-disable-line react-hooks/exhaustive-deps
  // Which geography *types* this org is scoped on, so callers can gate
  // per-type behavior (e.g. single-option auto-select) to the org's geoscope.
  // Derived from the counts rather than computed separately: "in scope" IS
  // "covers at least one", and two passes could drift apart.
  const scopeTypes = useMemo(
    () => Object.fromEntries(Object.entries(scopeCounts).map(([key, n]) => [key, n > 0])),
    [scopeCounts]
  );

  return {
    state,
    scopeLevel,
    scopeTypes,
    // Per-type sizes behind scopeTypes, for callers that have to say how big the scope is rather
    // than only whether it exists.
    scopeCounts,
    // The raw rows, so a caller can read the org's actual districts rather than only whether it
    // has any. Already fetched and shaped above; withholding them just made callers re-query.
    votingAreas,
    // On an org switch, Apollo resets `data` to undefined while the new
    // org's query is in flight - `state` is indistinguishable from "this
    // org genuinely has no voting area mapped to a state" without this,
    // which previously caused callers (report-dashboard/index.js's
    // `stateMissing`) to flash that warning for every org switch.
    loading,
  };
};
